import { useDebouncedQueryParam } from '../../../hooks';
import type { AdType } from '../types';
import type { SearchLanguages } from '../../../types/shared';
import {
  BooleanParam,
  createEnumArrayParam,
  DateParam,
  NumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params';
import { DEFAULT_PERIOD_PARAM } from '../constants';
import { sub } from 'date-fns';

const PeriodParam = withDefault(StringParam, DEFAULT_PERIOD_PARAM);
const OrderParam = withDefault(StringParam, 'max_impression:desc');
const BranchParam = withDefault(NumericArrayParam, []);
const PageNameParam = withDefault(NumericArrayParam, []);
const CompanyParam = withDefault(NumericArrayParam, []);
const AdTypeParam = withDefault(createEnumArrayParam<AdType>(['carousel', 'html', 'image', 'video']), [] as AdType[]);
const DomainsParam = withDefault(NumericArrayParam, [] as number[]);
const FlowsParam = withDefault(NumericArrayParam, [] as number[]);
const TagsParam = withDefault(NumericArrayParam, [] as number[]);
const ChannelsParam = withDefault(NumericArrayParam, [] as number[]);
const FirstSeenParam = withDefault(BooleanParam, false);
const AppsParam = withDefault(NumericArrayParam, [] as number[]);
const LangParam = withDefault(createEnumArrayParam<SearchLanguages>(['en', 'es', 'pt', 'un']), [] as SearchLanguages[]);
const StartDateParam = withDefault(DateParam, sub(new Date(), { weeks: 1 }));
const EndDateParam = withDefault(DateParam, new Date());

const useCreativesQueryParams = () => {
  const {
    param: domains,
    actualValue: actualDomains,
    handleChange: setDomains,
  } = useDebouncedQueryParam<typeof DomainsParam, number[]>({ name: 'domains', defaultValue: DomainsParam });
  const {
    param: apps,
    actualValue: actualApps,
    handleChange: setApps,
  } = useDebouncedQueryParam<typeof AppsParam, number[]>({ name: 'apps', defaultValue: AppsParam });
  const {
    param: flows,
    actualValue: actualFlows,
    handleChange: setFlows,
  } = useDebouncedQueryParam<typeof FlowsParam, number[]>({ name: 'flows', defaultValue: FlowsParam });
  const {
    param: tags,
    actualValue: actualTags,
    handleChange: setTags,
  } = useDebouncedQueryParam<typeof TagsParam, number[]>({ name: 'tags', defaultValue: TagsParam });
  const {
    param: channels,
    actualValue: actualChannels,
    handleChange: setChannels,
  } = useDebouncedQueryParam<typeof ChannelsParam, number[]>({ name: 'channels', defaultValue: ChannelsParam });
  const {
    param: adTypes,
    actualValue: actualAdTypes,
    handleChange: setAdTypes,
  } = useDebouncedQueryParam<typeof AdTypeParam, AdType[]>({ name: 'ad_types', defaultValue: AdTypeParam });

  const {
    param: languages,
    actualValue: actualLanguages,
    handleChange: setLanguages,
  } = useDebouncedQueryParam<typeof LangParam, SearchLanguages[]>({ name: 'lang', defaultValue: LangParam });

  const {
    param: branches,
    actualValue: actualBranches,
    handleChange: setBranches,
  } = useDebouncedQueryParam<typeof BranchParam, number[]>({ name: 'branch', defaultValue: BranchParam });

  const {
    param: pageName,
    actualValue: actualPageName,
    handleChange: setPageName,
  } = useDebouncedQueryParam<typeof PageNameParam, number[]>({ name: 'page_name', defaultValue: PageNameParam });
  const {
    param: company,
    actualValue: actualCompany,
    handleChange: setCompany,
  } = useDebouncedQueryParam<typeof CompanyParam, number[]>({ name: 'company', defaultValue: CompanyParam });

  const [firstSeen, setFirstSeen] = useQueryParam('first_seen', FirstSeenParam);
  const [period, setPeriod] = useQueryParam('period', PeriodParam);
  const [ordering, setOrdering] = useQueryParam('order_by', OrderParam);
  const [startDate, setStartDate] = useQueryParam('start_date', StartDateParam);
  const [endDate, setEndDate] = useQueryParam('end_date', EndDateParam);

  return {
    domains,
    setDomains,
    actualDomains,
    apps,
    setApps,
    actualApps,
    flows,
    setFlows,
    actualFlows,
    tags,
    setTags,
    actualTags,
    channels,
    setChannels,
    actualChannels,
    adTypes,
    setAdTypes,
    actualAdTypes,
    languages,
    setLanguages,
    actualLanguages,
    firstSeen,
    setFirstSeen,
    period,
    setPeriod,
    ordering,
    setOrdering,
    branches,
    actualBranches,
    setBranches,
    pageName,
    actualPageName,
    setPageName,
    company,
    actualCompany,
    setCompany,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  };
};

export default useCreativesQueryParams;
